import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import { QueryState } from "@/components/common/AsyncState";
import { fetchTemplates, updateTemplate } from "@/api/notifications";
import type { NotificationTemplate } from "@/types/notification";

function TemplateCard({ template }: { template: NotificationTemplate }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(template.text);
  const [sendSms, setSendSms] = useState(template.sendSms);
  const [sendTelegram, setSendTelegram] = useState(template.sendTelegram);
  const [enabled, setEnabled] = useState(template.enabled);

  const mutation = useMutation({
    mutationFn: () => updateTemplate(template.code, { text, sendSms, sendTelegram, enabled }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["templates"] }),
  });

  const dirty =
    text !== template.text || sendSms !== template.sendSms || sendTelegram !== template.sendTelegram || enabled !== template.enabled;

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          {template.name}
        </Typography>
        <FormControlLabel control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />} label="Включён" />
      </Stack>
      <TextField
        fullWidth
        multiline
        minRows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        helperText="Плейсхолдеры: {имя} {фио} {предмет} {группа} {учитель} {кабинет} {время} {дата} {сумма} {период} {просрочка} {филиал}"
      />
      <Stack direction="row" spacing={2} mt={1.5} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2}>
          <FormControlLabel control={<Switch checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />} label="SMS" />
          <FormControlLabel control={<Switch checked={sendTelegram} onChange={(e) => setSendTelegram(e.target.checked)} />} label="Telegram" />
        </Stack>
        <Button size="small" variant="contained" disabled={!dirty || mutation.isPending} onClick={() => mutation.mutate()}>
          Сохранить
        </Button>
      </Stack>
    </Paper>
  );
}

export function TemplatesTab() {
  const { data, isLoading, error } = useQuery({ queryKey: ["templates"], queryFn: fetchTemplates });

  return (
    <QueryState isLoading={isLoading} error={error}>
      <Stack spacing={2}>
        {(data ?? []).map((template) => (
          <TemplateCard key={template.code} template={template} />
        ))}
      </Stack>
    </QueryState>
  );
}
